import { StepName } from '../design-system/types';
import { DisplayDossierOverview } from '../resources/dossier/types';

import {
    orderByDate,
    orderByHeatLevel,
    orderByNewDossier,
    orderByNumberDesc,
    orderByTask,
    orderOnlyNewDossierByDate,
} from './operations';
import {
    NEW_REQUEST_STEP,
    DISCOVERY_STEP,
    QUOTE_CREATION_STEP,
    CONSTRUCTION_STEP,
    QUOTE_VALIDATED_STEP,
    POSTPONED_STEP,
    PAYMENT_CONFIRMED_STEP,
    TRIP_PREPARATION_STEP,
    TRAVELLING_STEP,
    TRIP_HAS_ENDED_STEP,
    CLOSED_STEP,
    CANCELLED_STEP,
    LOST_STEP,
    DEFERRED_TRIP_STEP,
} from './step';

export type SortRules =
    | string
    | Array<string | ((a: DisplayDossierOverview, b: DisplayDossierOverview) => number)>;

const sortRules = {
    [NEW_REQUEST_STEP]: [
        orderByNewDossier(),
        orderOnlyNewDossierByDate('requestDate'),

        orderByHeatLevel(),
        orderByTask([
            'new_request.contact',
            'new_request.follow_up',
            'new_request.break_up_and_lost',
        ]),
        orderByNumberDesc('rating'),
        orderByDate('project.expectedArrivalDate'),
    ],
    [DISCOVERY_STEP]: [
        orderByHeatLevel(),
        orderByTask([
            ['discovery.call', 'discovery.follow_up', 'discovery.mail'],
            'discovery.break_up_and_lost',
        ]),
        orderByDate('task.dueDate'),
        orderByNumberDesc('rating'),
    ],
    [QUOTE_CREATION_STEP]: [
        orderByHeatLevel(),
        orderByDate('task.dueDate'),
        orderByNumberDesc('project.budget.amountPerPax'),
        orderByDate('project.expectedArrivalDate'),
    ],
    [CONSTRUCTION_STEP]: [
        orderByHeatLevel(),
        orderByDate('task.dueDate'),
        orderByDate('discussion.lastTravellerToAgentMessageDate'),
        orderByNumberDesc('project.budget.amountPerPax'),
    ],
    [QUOTE_VALIDATED_STEP]: [
        orderByHeatLevel(),
        orderByTask([
            'quote_validated.send_payment_link',
            'quote_validated.send_register_form',
            [
                'quote_validated.follow_up_1',
                'quote_validated.follow_up_2',
                'quote_validated.follow_up_payment',
            ],
        ]),
        orderByDate('task.dueDate'),
        orderByDate('project.expectedArrivalDate'),
    ],
    [POSTPONED_STEP]: [orderByHeatLevel(), orderByDate('task.dueDate')],
    [PAYMENT_CONFIRMED_STEP]: [
        orderByDate('task.dueDate'),
        orderByTask([
            ['payment_confirmed.book', 'payment_confirmed.send_confirmation_booking'],
            'payment_confirmed.send_payment_link',
            ['payment_confirmed.follow_up_1', 'payment_confirmed.follow_up_2'],
            'payment_confirmed.send_roadbook',
        ]),
        orderByDate('project.expectedArrivalDate'),
    ],
    [DEFERRED_TRIP_STEP]: [orderByDate('task.dueDate')],
    [TRIP_PREPARATION_STEP]: [
        orderByDate('task.dueDate'),
        orderByTask([
            'trip_preparation.send_payment_link',
            'trip_preparation.follow_up_payment',
            'trip_preparation.send_roadbook',
        ]),
        orderByDate('project.expectedArrivalDate'),
    ],
    [TRAVELLING_STEP]: [orderByDate('discussion.lastTravellerToAgentMessageDate')],
    [TRIP_HAS_ENDED_STEP]: [orderByDate('discussion.lastTravellerToAgentMessageDate')],
    [CLOSED_STEP]: [orderByDate('archiveDate', false)],
    [CANCELLED_STEP]: [orderByDate('archiveDate', false)],
    [LOST_STEP]: [orderByDate('archiveDate', false)],
};

export const getSortRulesByStepName = (name: StepName): SortRules => sortRules[name];
